import { v4 as uuidv4 } from 'uuid';
import { IParsedExercise, IParsedGroup, IParsedWorkoutResponse } from 'types/workout/parsed-workout';
import { IPlanExercise, IPlanGroup, ISetConfiguration } from 'types/workout/plan-state';
import { ExerciseGroupType } from 'types/workout/group-type';

export type ParsedGroupsInput = IParsedGroup[];

/**
 * Mapea el ejercicio parseado por la IA a la representacion local del editor
 * (IPlanExercise). Si la IA matcheo el rawName contra el catalogo, usa ese
 * exerciseId y el nombre de la mejor sugerencia. Si no, deja `exercise.id`
 * en null y guarda el rawName como placeholder para que el coach pueda
 * elegir / crear el ejercicio inline desde el dropdown.
 */
const mapExercise = (parsed: IParsedExercise, rowIndex: number): IPlanExercise => {
  const matchedExerciseId = parsed.match.exerciseId;
  // Cuando hay match, preferimos el nombre canonico del catalogo (primer
  // sugerencia) — asi el coach ve "Press de banca plano con barra" en vez
  // del crudo "press plano". Si no hay sugerencia (caso match.status='none'),
  // usamos el rawName tal cual como placeholder.
  const topSuggestion = parsed.match.suggestions[0];
  const displayName = matchedExerciseId !== null && topSuggestion ? topSuggestion.name : parsed.rawName;

  return {
    id: uuidv4(),
    dragId: uuidv4(),
    exercise: {
      id: matchedExerciseId,
      name: displayName
    },
    rowIndex,
    sets: parsed.sets ?? '',
    repetitions: parsed.repetitions ?? '',
    weight: parsed.weight ?? '',
    time: '',
    restInterval: parsed.restInterval ?? '',
    tempo: parsed.tempo ?? '',
    notes: parsed.notes ?? '',
    difficulty: parsed.difficulty ?? '',
    duration: '',
    distance: '',
    rpe: '',
    setConfiguration: parsed.setConfiguration as ISetConfiguration[] | null
  };
};

const mapGroup = (parsed: IParsedGroup, index: number): IPlanGroup => {
  const groupNumber = index + 1;
  const groupType = parsed.groupType ?? ExerciseGroupType.STANDARD;
  const now = new Date();
  return {
    id: uuidv4(),
    groupNumber,
    name: parsed.name ?? `Grupo ${groupNumber}`,
    set: '',
    rest: '',
    isRestPeriod: groupType === ExerciseGroupType.REST,
    groupType,
    // Coach mode no soporta tabata/emom/amrap → forzamos null incluso si el
    // backend devuelve algo (defensivo).
    groupConfig: null,
    restDuration: null,
    exercises: (parsed.exercises ?? []).map((ex, i) => mapExercise(ex, i)),
    createdAt: now,
    updatedAt: now
  };
};

/**
 * Convierte la respuesta parseada por la IA (`/workout/import/parse`) en el
 * shape que usa el editor `NewCreatePlan` (`IPlanGroup[]`).
 *
 * Reglas:
 *  - `match.exerciseId` no null → `exercise.id` queda con ese id, y el `name`
 *    sale de la primer sugerencia del matcher (nombre canonico del catalogo).
 *  - `match.exerciseId === null` → `exercise.id = null`, `exercise.name = rawName`
 *    (placeholder visible en el dropdown para que el coach lo corrija o lo
 *    cree como ejercicio nuevo).
 *  - `groupConfig` siempre null — en coach mode solo soportamos standard / rest.
 *  - Cada grupo y ejercicio recibe un uuid local (id) + dragId para el editor.
 */
export const parsedWorkoutToPlanGroups = (parsed: IParsedWorkoutResponse): IPlanGroup[] => {
  return parsedGroupsToPlanGroups(parsed.groups ?? []);
};

/**
 * Variante que toma directamente el array de `IParsedGroup` (útil para el
 * import de ciclo, donde cada día devuelve sus propios groups).
 */
export const parsedGroupsToPlanGroups = (groups: ParsedGroupsInput): IPlanGroup[] => {
  return (groups ?? []).map((group, index) => mapGroup(group, index));
};
